import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import log from "logbeacon";
import { useQueryClient } from "@tanstack/react-query";
import { useShellRouter } from "@/src/hooks";
import { EdgenModalCreateAble } from "../EdgenModal";
import NextImage from "../NextImage";
import { LOCAL_STORAGE_KEYS, ROUTER_PATH } from "@/src/config";

import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/modal/AlphaUnlockedModal";
import { opportunitiesQueryKeys } from "@/src/hooks/useOpportunities";
import { usePathname } from "@/src/hooks/usePathname";

const I18n = I18nUtil.createScoped(Translations);
// import { AlphaUnlockedModalProps } from "./types";

interface AlphaUnlockedModalProps {
  onClose?: () => void;
  isMobile?: boolean;
}

const contentItem = (icon: string, title: string, number: string) => {
  return (
    <div className="flex flex-row w-full items-center justify-between">
      <div className="flex flex-row items-center justify-center gap-2">
        <Icon
          name={icon}
          size={20}
          color={ospThemes.colors.content_brand_primary}
        />
        <span className="body_m text-content_primary">{title}</span>
      </div>
      <span className="body_bold_s text-content_primary px-[12.5px] bg-background_white_overlay10 rounded-[14px]">{`x${number}`}</span>
    </div>
  );
};

const mobileContentItem = (icon: string, title: string, number: string) => {
  return (
    <div className="flex flex-col w-full  bg-background_inverse_overlay70 border-solid border-[1px] border-background_white_overlay20 rounded-[12px] p-3 relative overflow-hidden gradient-border-task-center">
      <div className="relative z-10 flex flex-col gap-4">
        <div className="flex flex-row justify-between">
          <div className="relative flex items-center justify-center w-10 h-10 rounded-2 bg-background_brand_overlay20 overflow-hidden">
            <div className="absolute left-[-31.75px] top-[-27.25px] w-[64.691px] h-[60.978px] flex items-center justify-center pointer-events-none">
              <div
                className="w-[32.813px] h-[56.595px] rounded-full rotate-[51.34deg] blur-[8.8px] opacity-30"
                style={{
                  background:
                    "linear-gradient(180deg, #9AF3FF 0%, rgba(255, 255, 255, 0) 100%)",
                }}
              />
            </div>
            <div className="absolute left-[10.8px] top-[23.6px] w-[43.373px] h-[40.884px] flex items-center justify-center pointer-events-none">
              <div
                className="w-[22px] h-[37.945px] rounded-full rotate-[51.34deg] blur-[8.8px] opacity-20"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(154, 243, 255, 0.5) 0%, rgba(255, 255, 255, 0) 100%)",
                }}
              />
            </div>
            <Icon
              name={icon}
              size={24}
              color={ospThemes.colors.content_primary}
            />
          </div>
          <div
            className="relative flex items-center justify-center bg-content_brand_primary w-9 h-4 rounded-3 border-[1.043px] border-solid overflow-hidden"
            style={{
              borderColor: "var(--edgen-border_white_transparent50)",
            }}
          >
            <span className="body_bold_s text-content_inverse_primary">{`x${number}`}</span>
            <div
              className="absolute inset-0 pointer-events-none rounded-[inherit]"
              style={{
                boxShadow:
                  "inset 0px 3.128px 10.426px 0px rgba(255, 255, 255, 0.4)",
              }}
            />
          </div>
        </div>

        <div className="flex flex-row items-center justify-between w-full">
          <span className="title_m text-content_primary">{title}</span>
        </div>
      </div>
    </div>
  );
};

const AlphaUnlockedModalContent = (props: AlphaUnlockedModalProps) => {
  const itemsConfig = [
    {
      icon: "line-chart",
      title: I18n.t("Latest Edgen's Pick"),
      number: "1",
    },
    {
      icon: "calendar",
      title: I18n.t("Earnings Strategy"),
      number: "1",
    },
    {
      icon: "candles",
      title: I18n.t("HK IPO Strategy"),
      number: "1",
    },
    {
      icon: "credit",
      title: I18n.t("Analysis Credits"),
      number: "20",
    },
  ];
  const { onClose, isMobile = true } = props;
  const router = useShellRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const handleEnterAlpha = async () => {
    log.error("[AlphaUnlockedModalContent] 关闭了--");
    onClose?.();
    localStorage.setItem(LOCAL_STORAGE_KEYS.ALPHA_GIFT_COUNT, "1");
    await queryClient.invalidateQueries({
      queryKey: [...opportunitiesQueryKeys.all, "summary"],
    });
    if (pathname?.includes(ROUTER_PATH.ALPHA_PICK)) {
      return;
    }
    router.push(ROUTER_PATH.ALPHA_PICK);
  };

  // Mobile layout
  if (isMobile) {
    return (
      <div className="relative flex flex-col items-center justify-center bg-center bg-no-repeat">
        <NextImage
          src="/assets/images/bg-app-unlock.svg"
          alt="Alpha Unlocked Background"
          width={393}
          height={428}
          className="absolute top-0 left-0 w-full h-full object-cover z-0 pointer-events-none"
        />
        <span
          className="inline-block text-[42px] leading-[48px] font-medium bg-clip-text text-transparent tracking-[-1.2px]"
          style={{
            backgroundImage:
              "linear-gradient(113.855deg, rgb(169, 245, 255) 11.343%, rgb(30, 209, 233) 46.08%, rgb(169, 245, 255) 103.5%)",
          }}
        >
          {I18n.t("Congrats!")}
        </span>
        <span className="body_l text-content_primary">
          {I18n.t("Obtained the following benefits")}
        </span>
        <div className="grid grid-cols-2 gap-2 w-full mt-8 p-3">
          {itemsConfig.map((item, index) => (
            <div key={index}>
              {mobileContentItem(item.icon, item.title, item.number)}
            </div>
          ))}
        </div>
        <button
          aria-label="Alpha Unlocked Modal Content"
          data-openreplay-label={`Alpha Unlocked Modal Content Button | ${I18n.t("Got it !")}`}
          id="btn-alpha-unlocked-modal-content"
          className="button button_m bg-background_white_overlay100 text-content_inverse_primary flex items-center justify-center w-[128px] h-[44px] mt-10 rounded-[12px] cursor-pointer"
          onClick={handleEnterAlpha}
        >
          {I18n.t("Got it !")}
        </button>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className={cn("flex flex-col items-center justify-center pt-6 px-6")}>
      <div className="flex items-center justify-center w-[72px] h-[72px] rounded-full bg-background_white_overlay5">
        <Icon
          name="gift"
          size={40}
          color={ospThemes.colors.content_brand_primary}
        />
      </div>
      <span className="headline_regular_s text-content_primary mt-4">
        {I18n.t("Alpha Unlocked")}
      </span>
      <span className="body_m text-content_secondary mt-1">
        {I18n.t("Your starter pack is ready.")}
      </span>
      <div className="flex flex-col w-full gap-4 items-center justify-center mt-4 bg-background_white_overlay5 rounded-[12px] p-4">
        {itemsConfig.map((item) =>
          contentItem(item.icon, item.title, item.number),
        )}
      </div>
      <button
        aria-label="Alpha Unlocked Modal Content"
        data-openreplay-label={`Alpha Unlocked Modal Content Button | ${I18n.t("Enter Alpha")}`}
        id="btn-alpha-unlocked-modal-content-2"
        className="button button_m bg-background_white_overlay100 text-content_inverse_primary flex items-center justify-center w-full h-[48px] mt-6 rounded-[12px]"
        onClick={handleEnterAlpha}
      >
        {I18n.t("Enter Alpha")}
      </button>
    </div>
  );
};

export const AlphaUnlockedModalCreateAble = async (
  props?: AlphaUnlockedModalProps,
): Promise<boolean> => {
  const { isMobile, onClose } = props ?? {
    isMobile: false,
    onClose: () => {},
  };

  const onModalClose = () => {
    onClose?.();
    EdgenModalCreateAble.end(false);
  };

  return new Promise(() => {
    EdgenModalCreateAble.call({
      children: (
        <AlphaUnlockedModalContent onClose={onModalClose} isMobile={isMobile} />
      ),
      containerClassName: cn(
        isMobile
          ? "w-full h-[428px] !rounded-[0px]"
          : "w-[400px] h-[448px] bg-background_solid_secondary",
      ),
      contentStyle: {
        borderRadius: isMobile ? "16px 16px 0 0" : 16,
        width: isMobile ? "100%" : "400px",
        height: isMobile ? "428px" : "448px",
        padding: 0,
        overflow: "hidden",
        backgroundColor: "transparent",
      },
      containerStyle: {
        maxHeight: isMobile ? "428px" : "448px",
      },
      destroyOnClose: true,
      onCancel: onClose,
      showClose: false,
      centered: true,
    });
  });
};
